/**
 * Calling-campaign filters live in the URL (`/staff?section=winback&region=APAC&region=EMEA`),
 * so a refresh keeps them and a filtered view can be shared as a link.
 *
 * A spec names each key and its kind; values equal to their default are left
 * out of the URL. Lists are repeated params, which is also what the backend
 * reads (`List[str] = Query([])`). Other params on the URL (`section`) are kept.
 */
import * as React from "react";

export type FieldKind = "list" | "string" | "bool";
export type FilterSpec = Record<string, FieldKind>;

export type FilterValues<S extends FilterSpec> = {
  [K in keyof S]: S[K] extends "list" ? string[] : S[K] extends "bool" ? boolean : string | null;
};

function sameList(a: string[], b: string[]) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function isDefault<S extends FilterSpec>(spec: S, key: keyof S, value: unknown, defaults: FilterValues<S>): boolean {
  const d = defaults[key] as unknown;
  if (spec[key] === "list") return sameList(value as string[], d as string[]);
  return (value ?? null) === (d ?? null);
}

export function parseFilters<S extends FilterSpec>(spec: S, defaults: FilterValues<S>, search: URLSearchParams): FilterValues<S> {
  const out = { ...defaults } as Record<string, unknown>;
  for (const [key, kind] of Object.entries(spec)) {
    if (!search.has(key)) continue;
    if (kind === "list") {
      out[key] = Array.from(new Set(search.getAll(key).flatMap((v) => v.split(",")).map((v) => v.trim()).filter(Boolean)));
    } else if (kind === "bool") {
      out[key] = search.get(key) === "true";
    } else {
      out[key] = search.get(key) || null;
    }
  }
  return out as FilterValues<S>;
}

/** `base` with every spec key replaced by `values` (defaults omitted). */
export function serializeFilters<S extends FilterSpec>(
  spec: S, defaults: FilterValues<S>, values: FilterValues<S>, base = new URLSearchParams(),
): URLSearchParams {
  const p = new URLSearchParams(base);
  for (const [key, kind] of Object.entries(spec)) {
    p.delete(key);
    const v = (values as Record<string, unknown>)[key];
    if (isDefault(spec, key, v, defaults)) continue;
    if (kind === "list") for (const item of v as string[]) p.append(key, item);
    else if (kind === "bool") p.set(key, v ? "true" : "false");
    else if (v) p.set(key, String(v));
  }
  return p;
}

/** How many filters differ from their defaults — the "Filters (3)" badge. */
export function countActive<S extends FilterSpec>(spec: S, defaults: FilterValues<S>, values: FilterValues<S>, keys?: (keyof S)[]): number {
  return (keys ?? (Object.keys(spec) as (keyof S)[])).filter((k) => !isDefault(spec, k, values[k], defaults)).length;
}

export function useUrlFilters<S extends FilterSpec>(spec: S, defaults: FilterValues<S>) {
  const [values, setValues] = React.useState<FilterValues<S>>(() =>
    typeof window === "undefined" ? defaults : parseFilters(spec, defaults, new URLSearchParams(window.location.search)),
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const next = serializeFilters(spec, defaults, values, new URLSearchParams(window.location.search));
    const qs = next.toString();
    const url = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
    if (url !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      // replaceState, not push: each chip click is not a page the Back button should step through.
      window.history.replaceState(window.history.state, "", url);
    }
  }, [spec, defaults, values]);

  const update = React.useCallback((patch: Partial<FilterValues<S>>) => setValues((cur) => ({ ...cur, ...patch })), []);
  const reset = React.useCallback((keep: Partial<FilterValues<S>> = {}) => setValues({ ...defaults, ...keep }), [defaults]);
  /** Adds or removes one value of a list filter. */
  const toggle = React.useCallback((key: keyof S, value: string) => setValues((cur) => {
    const list = (cur[key] as unknown as string[]) ?? [];
    const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
    return { ...cur, [key]: next };
  }), []);

  return { values, update, reset, toggle };
}
